// HeroSection.js — Sección hero dinámica con personalización completa
import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { BACKEND_BASE } from '../../../services/api';
import { buildBg } from '../bgHelpers';
import { useIdioma } from '../../../contexts/IdiomaContext';
import { useAuth } from '../../../contexts/AuthContext';
import { resolveDeviceValue } from '../../admin/DeviceTabsSelect';
import { subrayadoCss } from '../tituloHelpers';

// Defaults visuales por code (mismos que HeroForm). El admin puede overridear por code.
const IDIOMA_DEFAULTS = {
  es: { label: 'Español',    flag_url: 'https://flagcdn.com/w40/es.png', color: '#c60b1e' },
  en: { label: 'English',    flag_url: 'https://flagcdn.com/w40/us.png', color: '#1e3a8a' },
  pt: { label: 'Português',  flag_url: 'https://flagcdn.com/w40/br.png', color: '#22c55e' },
  fr: { label: 'Français',   flag_url: 'https://flagcdn.com/w40/fr.png', color: '#0055a4' },
  it: { label: 'Italiano',   flag_url: 'https://flagcdn.com/w40/it.png', color: '#009246' },
  de: { label: 'Deutsch',    flag_url: 'https://flagcdn.com/w40/de.png', color: '#000000' },
};

const pulse = keyframes`
  0%, 100% { box-shadow: 0 4px 20px rgba(220, 38, 38, 0.2); }
  50% { box-shadow: 0 8px 30px rgba(220, 38, 38, 0.3); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  width: 100%;
  background-size: cover;
  background-position: ${p => p.$bgPosition || 'center'};
  ${p => p.$bgImage
    ? `background-image: url(${p.$bgImage}); background-color: ${p.$bgColor || '#0f355b'};`
    : `background: ${p.$bg || p.$bgColor || '#0f355b'};`}
  min-height: ${p => p.$minHeight || 'auto'};
  display: flex;
  align-items: center;
  position: ${p => p.$fixed ? 'fixed' : 'relative'};
  left: 0;
  ${p => p.$fixed ? (p.$pos === 'abajo' ? 'bottom: 0;' : 'top: 0;') : ''}
  z-index: ${p => p.$fixed ? 900 : 'auto'};
  ${p => p.$borderBottom ? `border-bottom: ${p.$borderBottom};` : ''}
  /* Clip cualquier efecto de luz (streak/aurora/glow) que se salga horizontalmente */
  overflow: hidden;
`;

const Overlay = styled.div`
  width: 100%;
  ${p => p.$overlay && `background: ${p.$overlay};`}
`;

const Inner = styled.div`
  max-width: ${p => p.$maxWidth || '1200px'};
  margin: 0 auto;
  display: flex;
  align-items: ${p => p.$verticalAlign || 'center'};
  padding: ${p => p.$padding || '2rem'};
  ${p => p.$paddingTop ? `padding-top: ${p.$paddingTop};` : ''}
  flex-direction: ${p => p.$layout === 'img-right' ? 'row-reverse' : (p.$layout === 'text-only' ? 'column' : 'row')};
  @media (max-width: 991px) {
    flex-wrap: wrap;
    padding: 1.5rem 1rem;
    ${p => p.$paddingTop ? `padding-top: ${p.$paddingTop};` : ''}
    flex-direction: column;
  }
`;

const ImgCol = styled.div`
  width: ${p => p.$width || '50%'};
  text-align: center;
  position: relative;
  @media (max-width: 991px) { width: 100%; }
`;

const MainImg = styled.img`
  width: 100%;
  max-height: ${p => p.$maxH || '450px'};
  object-fit: ${p => p.$fit || 'contain'};
  object-position: ${p => p.$pos || 'center'};
  border-radius: ${p => p.$radius || '0'};
  position: relative;
  z-index: 1;
`;

/* Animaciones para los efectos futuristas */
const pulseGlow = keyframes`
  0%, 100% { opacity: 0.65; transform: scale(1); }
  50%      { opacity: 1;    transform: scale(1.08); }
`;

const streakFlow = keyframes`
  0%   { stroke-dashoffset: 0;    opacity: 0.95; }
  50%  { opacity: 0.5; }
  100% { stroke-dashoffset: -400; opacity: 0.95; }
`;

const auroraShift = keyframes`
  0%, 100% { transform: translate(0,0) scale(1); }
  33%      { transform: translate(8%,-6%) scale(1.05); }
  66%      { transform: translate(-6%,5%) scale(0.97); }
`;

/* Galaxia: conic girando + estrellas titilando */
const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;
const twinkle = keyframes`
  0%, 100% { opacity: 0.35; }
  50%      { opacity: 1; }
`;
/* Luces circulares: anillos que se expanden y desvanecen */
const ringPulse = keyframes`
  0%   { transform: scale(0.35); opacity: 0; }
  18%  { opacity: 1; }
  100% { transform: scale(1.25); opacity: 0; }
`;
/* Luz fugaz: estrella que cruza en diagonal */
const shoot = keyframes`
  0%        { transform: translate(-30%, -20%) rotate(32deg); opacity: 0; }
  8%        { opacity: 1; }
  45%       { opacity: 1; }
  60%, 100% { transform: translate(180%, 120%) rotate(32deg); opacity: 0; }
`;
/* Título iluminado: glow pulsante */
const titleGlow = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.18); }
`;

/* Glow / círculo — efecto difuso detrás de la imagen, con animación opcional */
const EfectoImagenBg = styled.div`
  position: absolute;
  inset: -8%;
  pointer-events: none;
  z-index: 0;
  opacity: ${p => (p.$intensidad != null ? p.$intensidad : 0.8)};
  ${p => p.$tipo === 'glow' && css`
    background:
      radial-gradient(ellipse 70% 55% at 50% 55%, ${p.$color || '#ec4899'}cc 0%, ${p.$color || '#ec4899'}55 35%, transparent 70%),
      radial-gradient(circle 30% at 30% 30%, ${p.$color2 || '#8b5cf6'}66 0%, transparent 70%);
    filter: blur(25px) brightness(1.15);
  `}
  ${p => p.$tipo === 'circle' && css`
    background: radial-gradient(circle at 50% 50%, ${p.$color || '#ec4899'}88 0%, transparent 60%);
    filter: blur(35px) brightness(1.1);
  `}
  ${p => p.$tipo === 'aurora' && css`
    background:
      radial-gradient(circle 40% at 25% 30%, ${p.$color || '#ec4899'}99 0%, transparent 60%),
      radial-gradient(circle 35% at 70% 60%, ${p.$color2 || '#8b5cf6'}88 0%, transparent 60%),
      radial-gradient(circle 30% at 50% 80%, ${p.$color3 || '#06b6d4'}77 0%, transparent 60%);
    filter: blur(30px) brightness(1.1);
  `}

  /* ✦ GALAXIA — nebulosa luminosa + halo cónico girando + estrellas titilando */
  ${p => p.$tipo === 'galaxia' && css`
    background:
      radial-gradient(circle at 32% 36%, ${p.$color || '#ec4899'}cc 0%, transparent 45%),
      radial-gradient(circle at 68% 64%, ${p.$color2 || '#8b5cf6'}bb 0%, transparent 45%),
      radial-gradient(circle at 50% 50%, ${p.$color3 || '#06b6d4'}99 0%, transparent 62%),
      radial-gradient(circle at 50% 50%, rgba(255,255,255,0.85) 0%, transparent 16%);
    filter: blur(26px) brightness(1.2) saturate(1.2);
    &::before {
      content: ''; position: absolute; inset: -25%; border-radius: 50%;
      background: conic-gradient(from 0deg,
        ${p.$color || '#ec4899'}00, ${p.$color || '#ec4899'}77,
        ${p.$color2 || '#8b5cf6'}77, ${p.$color3 || '#06b6d4'}77,
        ${p.$color || '#ec4899'}00);
      filter: blur(38px);
      ${p.$animar && css`animation: ${spin} 16s linear infinite;`}
    }
    &::after {
      content: ''; position: absolute; inset: 0;
      background-image:
        radial-gradient(1.6px 1.6px at 18% 24%, #fff, transparent),
        radial-gradient(1.4px 1.4px at 72% 18%, #fff, transparent),
        radial-gradient(1.8px 1.8px at 58% 62%, #fff, transparent),
        radial-gradient(1.3px 1.3px at 33% 78%, #fff, transparent),
        radial-gradient(1.5px 1.5px at 85% 70%, #fff, transparent),
        radial-gradient(1.2px 1.2px at 45% 35%, #fff, transparent);
      ${p.$animar && css`animation: ${twinkle} 3.2s ease-in-out infinite;`}
    }
  `}

  /* ✦ LUCES CIRCULARES — anillos de luz que se expanden */
  ${p => p.$tipo === 'luces_circulares' && css`
    background: radial-gradient(circle at 50% 50%, ${p.$color || '#ec4899'}66 0%, transparent 38%);
    filter: blur(6px) brightness(1.15);
    &::before, &::after {
      content: ''; position: absolute; inset: 8%; border-radius: 50%;
      border: 2px solid ${p.$color2 || '#8b5cf6'};
      box-shadow: 0 0 45px ${p.$color || '#ec4899'}, inset 0 0 45px ${p.$color || '#ec4899'}aa;
      ${p.$animar && css`animation: ${ringPulse} 4.2s ease-out infinite;`}
    }
    &::after {
      border-color: ${p.$color3 || '#06b6d4'};
      box-shadow: 0 0 45px ${p.$color3 || '#06b6d4'}, inset 0 0 45px ${p.$color3 || '#06b6d4'}aa;
      ${p.$animar && css`animation: ${ringPulse} 4.2s ease-out infinite; animation-delay: 2.1s;`}
    }
  `}

  /* ✦ LUZ FUGAZ — estrellas fugaces cruzando + leve halo */
  ${p => p.$tipo === 'luz_fugaz' && css`
    overflow: hidden;
    background: radial-gradient(ellipse 60% 50% at 50% 50%, ${p.$color || '#ec4899'}3a 0%, transparent 65%);
    &::before {
      content: ''; position: absolute; top: 6%; left: 0;
      width: 42%; height: 2px; border-radius: 50%;
      background: linear-gradient(90deg, transparent, #fff 70%, ${p.$color2 || '#8b5cf6'});
      box-shadow: 0 0 10px 2px #fff, 0 0 26px 6px ${p.$color || '#ec4899'};
      transform: translate(-30%, -20%) rotate(32deg); opacity: 0;
      ${p.$animar && css`animation: ${shoot} 3.6s ease-in infinite;`}
    }
    &::after {
      content: ''; position: absolute; top: 2%; left: 18%;
      width: 32%; height: 2px; border-radius: 50%;
      background: linear-gradient(90deg, transparent, #fff 70%, ${p.$color3 || '#06b6d4'});
      box-shadow: 0 0 8px 2px #fff, 0 0 22px 5px ${p.$color3 || '#06b6d4'};
      transform: translate(-30%, -20%) rotate(32deg); opacity: 0;
      ${p.$animar && css`animation: ${shoot} 4.8s ease-in infinite; animation-delay: 2.2s;`}
    }
  `}

  ${p => p.$animar && (p.$tipo === 'glow' || p.$tipo === 'circle') && css`animation: ${pulseGlow} 4s ease-in-out infinite;`}
  ${p => p.$animar && p.$tipo === 'aurora' && css`animation: ${auroraShift} 9s ease-in-out infinite;`}
`;

/* Estela neón — SVG inline (animable con stroke-dashoffset) */
const StreakSvg = styled.svg`
  position: absolute;
  inset: -10%;
  width: 120%;
  height: 120%;
  pointer-events: none;
  z-index: 0;
  opacity: ${p => (p.$intensidad != null ? p.$intensidad : 0.85)};
  mix-blend-mode: screen;
  filter: brightness(1.25) saturate(1.25);
  .comet { ${p => p.$animar ? '' : 'display: none;'} }
  .core {
    stroke-dasharray: 200 600;
    ${p => p.$animar && css`animation: ${streakFlow} 6s linear infinite;`}
  }
  .core-2 {
    stroke-dasharray: 120 800;
    ${p => p.$animar && css`animation: ${streakFlow} 8s linear infinite reverse; animation-delay: -2s;`}
  }
`;

const ContentCol = styled.div`
  width: ${p => p.$center ? 'fit-content' : (p.$width || '50%')};
  ${p => p.$center && 'max-width: 100%; margin: 0 auto;'}
  padding: ${p => p.$padding || '2rem'};
  animation: ${fadeInUp} 1s ease-out;
  text-align: ${p => p.$align || 'center'};
  @media (max-width: 991px) and (min-width: 601px) { width: 100%; text-align: ${p => p.$alignTablet || p.$align || 'center'}; padding: 1rem; }
  @media (max-width: 600px) { width: 100%; text-align: ${p => p.$alignMobile || p.$alignTablet || p.$align || 'center'}; padding: 1rem; }
`;

const Title = styled.h1`
  font-size: ${p => p.$size || '52px'};
  color: ${p => p.$color || '#ffffff'};
  font-family: ${p => p.$font || "'Myriad Pro', sans-serif"};
  font-weight: ${p => p.$weight || '700'};
  margin: 0 0 0.5rem;
  line-height: ${p => p.$lineHeight || '1.15'};
  text-align: ${p => p.$align || 'inherit'};
  text-transform: ${p => p.$transform || 'none'};
  ${p => p.$glow && css`
    text-shadow:
      0 0 8px ${p.$glowColor}dd,
      0 0 22px ${p.$glowColor}aa,
      0 0 44px ${p.$glowColor}77;
    ${p.$glowAnim && css`animation: ${titleGlow} 3s ease-in-out infinite;`}
  `}
  ${p => p.$subrayado || ''}
  @media (max-width: 991px) and (min-width: 601px) { font-size: ${p => parseInt(p.$size || 52) * 0.65}px; text-align: ${p => p.$alignTablet || p.$align || 'inherit'}; }
  @media (max-width: 600px) { font-size: ${p => parseInt(p.$size || 52) * 0.65}px; text-align: ${p => p.$alignMobile || p.$alignTablet || p.$align || 'inherit'}; }
`;

/* Icono junto al titular (el portátil amarillo de HepCampus). Sin
   icono_titulo no se pinta nada y el hero queda como siempre. */
const TituloFila = styled.div`
  display: flex;
  align-items: ${p => p.$align || 'center'};
  gap: ${p => p.$gap || '18px'};
  justify-content: ${p => p.$justify || 'flex-start'};
  @media (max-width: 600px) { gap: ${p => p.$gapMovil || '12px'}; }
`;

const TituloIcono = styled.img`
  flex: 0 0 auto;
  width: ${p => p.$ancho || '100px'};
  height: auto;
  display: block;
  @media (max-width: 600px) { width: ${p => p.$anchoMovil || '64px'}; }
`;

const Subtitle = styled.div`
  font-size: ${p => p.$size || '19px'};
  color: ${p => p.$color || '#ffffff'};
  line-height: ${p => p.$lineHeight || '1.6'};
  font-family: ${p => p.$font || "'Myriad Pro', sans-serif"};
  font-weight: ${p => p.$weight || '300'};
  text-align: ${p => p.$align || 'inherit'};
  margin-bottom: ${p => p.$mb || '1rem'};
  @media (max-width: 991px) and (min-width: 601px) { line-height: ${p => p.$lineHeight || '1.3'}; text-align: ${p => p.$alignTablet || p.$align || 'inherit'}; }
  @media (max-width: 600px) { line-height: ${p => p.$lineHeight || '1.3'}; text-align: ${p => p.$alignMobile || p.$alignTablet || p.$align || 'inherit'}; }
`;

const ExtraText = styled.div`
  font-size: ${p => p.$size || '16px'};
  color: ${p => p.$color || '#ffffff'};
  line-height: ${p => p.$lineHeight || '1.6'};
  font-family: ${p => p.$font || "'Myriad Pro', sans-serif"};
  font-weight: ${p => p.$weight || '400'};
  text-align: ${p => p.$align || 'inherit'};
  margin-bottom: ${p => p.$mb || '1rem'};
  @media (max-width: 991px) { line-height: ${p => p.$lineHeight || '1.4'}; }
`;

const IdiomasRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${p => p.$gap || '1.5rem'};
  margin-top: ${p => p.$mt || '1.5rem'};
  justify-content: ${p => p.$justify || 'flex-start'};
  align-items: flex-start;
`;

/* Pill / toggle simple — estilo "pills" o "toggle" */
const IdiomaBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: ${p => p.$pillar ? '0.45rem 1rem' : '0.4rem 0.8rem'};
  background: ${p => p.$active
    ? (p.$pillar ? (p.$color || '#0f355b') : 'rgba(255,255,255,0.18)')
    : (p.$pillar ? 'rgba(255,255,255,0.25)' : 'transparent')};
  color: ${p => p.$active && p.$pillar ? '#fff' : (p.$textColor || '#fff')};
  border: ${p => p.$active ? `2px solid ${p.$color || '#fff'}` : '2px solid rgba(255,255,255,0.4)'};
  border-radius: ${p => p.$pillar ? '999px' : '8px'};
  font-weight: ${p => p.$active ? 700 : 500};
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  &:hover { transform: translateY(-1px); opacity: 0.9; }
  img { width: 22px; height: 22px; object-fit: cover; border-radius: 3px; }
`;

/* Bandera estilo "shield" (escudo redondeado) con etiqueta debajo — referencia banderas.png */
const IdiomaShield = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  font-family: inherit;
  transition: transform 0.2s;
  &:hover { transform: translateY(-2px); }
  &:focus { outline: none; }
`;

const IdiomaShieldImg = styled.div`
  width: ${p => p.$size || '64px'};
  height: ${p => p.$size || '64px'};
  border-radius: 42% 42% 50% 50% / 35% 35% 65% 65%;
  background-image: url(${p => p.$flag});
  background-size: cover;
  background-position: center;
  box-shadow: 0 6px 18px rgba(0,0,0,0.35), inset 0 -8px 14px rgba(0,0,0,0.18), inset 0 8px 14px rgba(255,255,255,0.25);
  border: ${p => p.$showBorder ? `3px solid ${p.$borderColor || '#fff'}` : 'none'};
  outline: ${p => p.$active ? `3px solid ${p.$activeColor || '#14b6cb'}` : 'none'};
  outline-offset: 3px;
  transition: outline 0.2s, transform 0.2s;
  ${IdiomaShield}:hover & { transform: scale(1.04); }
`;

const IdiomaShieldLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
  font-weight: ${p => p.$active ? 800 : 600};
  font-size: ${p => p.$size || '0.85rem'};
  color: ${p => p.$color || '#fff'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.1;
  text-align: center;
  small {
    font-size: 0.78em;
    font-weight: 400;
    text-transform: none;
    opacity: 0.85;
    letter-spacing: 0;
  }
`;

const BtnRow = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: ${p => p.$justify || 'center'};
  flex-wrap: wrap;
  margin-top: ${p => p.$mt || '1rem'};
  @media (max-width: 991px) and (min-width: 601px) { justify-content: ${p => p.$justifyTablet || p.$justify || 'center'}; }
  @media (max-width: 600px) { justify-content: ${p => p.$justifyMobile || p.$justifyTablet || p.$justify || 'center'}; }
`;

const Btn = styled.button`
  background: ${p => p.$bg || '#0f355b'};
  color: ${p => p.$color || '#fff'};
  padding: ${p => p.$padding || '10px 32px'};
  border-radius: ${p => p.$radius || '8px'};
  font-weight: bold;
  font-size: ${p => p.$size || '17px'};
  font-family: inherit;
  border: ${p => p.$border || 'none'};
  text-transform: ${p => p.$transform || 'none'};
  cursor: pointer;
  transition: 0.3s;
  animation: ${pulse} 3s infinite;
  &:hover { transform: translateY(-2px); animation: none; opacity: 0.9; }
  ${p => p.$sizeTablet ? `@media (max-width: 991px) and (min-width: 601px) { font-size: ${p.$sizeTablet}; }` : ''}
  ${p => p.$sizeMobile ? `@media (max-width: 600px) { font-size: ${p.$sizeMobile}; }` : ''}
`;

const resolveUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${BACKEND_BASE}/${url}`;
};

const HeroSection = ({ config, proyecto, openLogin, openRegister, tl }) => {
  const c = config;
  const { idioma, setIdioma, t } = useIdioma();
  const { user } = useAuth();
  // Si el usuario ya inició sesión, los botones de Iniciar sesión / Registro del
  // hero no tienen sentido → se ocultan (los botones con acción custom/URL se mantienen).
  const accionLogin = c.accion_boton_login || 'login';
  const accionRegistro = c.accion_boton_registro || 'registro';
  const mostrarBtnLogin = (c.boton_login !== false) && !(user && accionLogin === 'login');
  const mostrarBtnRegistro = (c.boton_registro !== false) && !(user && accionRegistro === 'registro');
  // ID estable y único por instancia para los <defs> del SVG (filter/gradient)
  const uid = React.useId().replace(/:/g, '');
  const primary = c.color_primario || proyecto?.color_primario || '#0f355b';
  const secondary = c.color_secundario || proyecto?.color_secundario || '#14b6cb';

  // Flujo "idioma primero": cuando está activo + hay banderas configuradas, los
  // botones de Login/Registro SE OCULTAN del hero. Cada click en una bandera
  // cambia el idioma y reabre el AuthModal en el tab configurado. El usuario
  // puede alternar Login/Registro desde las tabs internas del modal.
  const flujoIdiomaPrimero = c.flujo_idioma_primero === true && c.mostrar_idiomas === true;
  const mostrarLogin = c.boton_login !== false;
  const mostrarRegistro = c.boton_registro !== false;

  const abrirAuthModalDesdeIdioma = () => {
    const tabConfigurado = c.tab_inicial_idioma_primero || 'register';
    // En el modal: sólo mostramos los tabs cuyos botones del hero estén activos.
    // Si el hero sólo tiene "Registro" activo, el modal arranca sin tab de Login.
    const opts = { showLoginTab: mostrarLogin, showRegisterTab: mostrarRegistro };
    if (mostrarRegistro && (tabConfigurado === 'register' || !mostrarLogin)) {
      openRegister?.(opts);
    } else if (mostrarLogin) {
      openLogin?.(opts);
    }
  };

  const onSelectIdioma = (code) => {
    setIdioma(code);
    if (flujoIdiomaPrimero && !user) {
      // Cada click reabre el modal — incluyendo después de cerrarlo (no si ya inició sesión)
      setTimeout(() => abrirAuthModalDesdeIdioma(), 280);
    }
  };

  // En el flujo idioma-primero los botones quedan permanentemente en el modal
  const ocultarBotones = flujoIdiomaPrimero;

  // Resolver labels: si el admin no puso texto custom, usar la traducción built-in
  // que cambia con el idioma seleccionado. Si puso un objeto {es,en,pt}, tl() lo
  // resuelve por idioma. Si puso un string fijo, se respeta tal cual.
  const textoLogin = tl(c.texto_boton_login, '') || t('hero_btn_login');
  const textoRegistro = tl(c.texto_boton_registro, '') || t('hero_btn_registro');
  const textoElegirIdioma = c.texto_elegir_idioma === ''
    ? ''
    : (tl(c.texto_elegir_idioma, '') || t('hero_elegir_idioma'));

  const bgImg = resolveUrl(c.imagen_fondo);
  const mainImg = resolveUrl(c.imagen_principal);

  // Layout: img-left (default), img-right, text-only
  const layout = c.layout || (mainImg ? 'img-left' : 'text-only');
  // Alineación del texto por dispositivo. `alineacion_texto` puede ser string
  // (legacy: aplica a todos) u objeto {mobile,tablet,desktop} (device-tabs).
  const alignR = resolveDeviceValue(c.alineacion_texto, 'center');
  const normAlign = (a) => (a === 'izq-centrado' ? 'left' : (a || 'center'));
  // 'izq-centrado' = texto alineado a la izquierda pero el bloque centrado en la página
  // El bloque se centra horizontalmente en su columna si:
  //   - El admin eligió "izq-centrado" en escritorio, o
  //   - Activó el toggle `centrar_bloque` (centrar bloque para cualquier alineación)
  const blockCentered = alignR.desktop === 'izq-centrado' || c.centrar_bloque === true;
  const textAlign = normAlign(alignR.desktop);
  const textAlignTablet = normAlign(alignR.tablet);
  const textAlignMobile = normAlign(alignR.mobile);

  // Alineación PROPIA del título por dispositivo (override opcional). Si un
  // dispositivo queda vacío, hereda la alineación general del texto.
  const tAlignR = resolveDeviceValue(c.alineacion_titulo, '');
  const titleAlign = tAlignR.desktop ? normAlign(tAlignR.desktop) : textAlign;
  const titleAlignTablet = tAlignR.tablet ? normAlign(tAlignR.tablet) : textAlignTablet;
  const titleAlignMobile = tAlignR.mobile ? normAlign(tAlignR.mobile) : textAlignMobile;

  // Justify map for buttons (por dispositivo)
  const justifyFor = (a) => a === 'left' ? 'flex-start' : a === 'right' ? 'flex-end' : 'center';
  const btnJustify = justifyFor(textAlign);
  const btnJustifyTablet = justifyFor(textAlignTablet);
  const btnJustifyMobile = justifyFor(textAlignMobile);

  // Tamaño de fuente de los botones por dispositivo (login/registro).
  const sizeLoginR = resolveDeviceValue(c.tamano_boton_login || c.tamano_botones, '');
  const sizeRegistroR = resolveDeviceValue(c.tamano_boton_registro || c.tamano_botones, '');

  const handleAction = (action) => {
    const opts = { showLoginTab: c.boton_login !== false, showRegisterTab: c.boton_registro !== false };
    if (action === 'login') openLogin?.(opts);
    else if (action === 'registro') openRegister?.(opts);
    else if (action && action.startsWith('http')) window.open(action, '_blank');
  };

  const contentWidth = layout === 'text-only' ? '100%' : (c.ancho_contenido || '50%');
  const imgWidth = layout === 'text-only' ? '0%' : (c.ancho_imagen || '50%');

  // Bloque de "texto extra" (p.ej. "Avalado por: <img>"). Se puede posicionar
  // arriba del título con `texto_extra_arriba` (default: debajo del subtítulo).
  const extraTextNode = c.texto_extra ? (
    <ExtraText
      $color={c.color_texto_extra || '#fff'}
      $size={c.tamano_texto_extra || '16px'}
      $weight={c.peso_texto_extra || '400'}
      $font={c.fuente_texto_extra}
      $lineHeight={c.interlineado_texto_extra || '1.6'}
      $align={textAlign}
      $mb={c.margen_texto_extra}
      dangerouslySetInnerHTML={{ __html: tl(c.texto_extra, '') }}
    />
  ) : null;

  return (
    <Wrapper
      $bgColor={primary}
      $bg={buildBg(c, primary)}
      $bgImage={bgImg}
      $bgPosition={c.posicion_fondo || 'center'}
      $minHeight={c.pantalla_completa ? '100vh' : (c.altura_minima || 'auto')}
      $fixed={c.fijo}
      $pos={c.posicion_fija}
      $borderBottom={c.borde_inferior}
    >
      <Overlay $overlay={c.overlay_fondo}>
        <Inner
          $layout={layout}
          $padding={c.padding || '2rem'}
          $paddingTop={c.padding_superior}
          $maxWidth={c.ancho_maximo || '1200px'}
          $verticalAlign={c.alineacion_vertical || 'center'}
        >
          {layout !== 'text-only' && mainImg && (() => {
            const efecto = c.efecto_imagen_principal || 'ninguno';
            const efColor = c.efecto_color_1 || '#ec4899';
            const efColor2 = c.efecto_color_2 || '#8b5cf6';
            const efColor3 = c.efecto_color_3 || '#06b6d4';
            const efIntensidad = c.efecto_intensidad != null ? parseFloat(c.efecto_intensidad) : 0.85;
            const efAnimar = c.efecto_animar !== false; // animado por defecto
            const PATH_MAIN = "M30 340 Q 90 60 200 220 T 380 80";
            const PATH_ALT  = "M20 360 Q 140 280 180 180 T 390 160";
            return (
              <ImgCol $width={imgWidth}>
                {(efecto === 'glow' || efecto === 'circle' || efecto === 'aurora' || efecto === 'galaxia' || efecto === 'luces_circulares' || efecto === 'luz_fugaz') && (
                  <EfectoImagenBg
                    $tipo={efecto}
                    $color={efColor}
                    $color2={efColor2}
                    $color3={efColor3}
                    $intensidad={efIntensidad}
                    $animar={efAnimar}
                  />
                )}
                {efecto === 'streak' && (
                  <StreakSvg viewBox="0 0 400 400" preserveAspectRatio="none" $intensidad={efIntensidad} $animar={efAnimar}>
                    <defs>
                      <filter id={`hero-streak-glow-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
                        <feGaussianBlur stdDeviation="12" />
                      </filter>
                      <filter id={`hero-streak-glow-soft-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
                        <feGaussianBlur stdDeviation="6" />
                      </filter>
                      <linearGradient id={`hero-streak-grad-${uid}`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor={efColor} />
                        <stop offset="50%" stopColor={efColor2} />
                        <stop offset="100%" stopColor={efColor3} />
                      </linearGradient>
                      {/* Path para que las partículas viajen sobre él (offset-path en CSS) */}
                      <path id={`hero-streak-path-${uid}`} d={PATH_MAIN} />
                    </defs>

                    {/* Aura difuso muy grueso */}
                    <path
                      d={PATH_MAIN}
                      stroke={`url(#hero-streak-grad-${uid})`}
                      strokeWidth="28"
                      fill="none"
                      strokeLinecap="round"
                      filter={`url(#hero-streak-glow-${uid})`}
                      opacity="0.55"
                    />
                    {/* Brillo medio */}
                    <path
                      d={PATH_MAIN}
                      stroke={`url(#hero-streak-grad-${uid})`}
                      strokeWidth="10"
                      fill="none"
                      strokeLinecap="round"
                      filter={`url(#hero-streak-glow-soft-${uid})`}
                    />
                    {/* Línea fina central (núcleo neón) — animada con stroke-dashoffset */}
                    <path
                      className="core"
                      d={PATH_MAIN}
                      stroke="#ffffff"
                      strokeWidth="2.5"
                      fill="none"
                      strokeLinecap="round"
                      opacity="0.95"
                    />
                    {/* Segunda curva más sutil que cruza */}
                    <path
                      d={PATH_ALT}
                      stroke={`url(#hero-streak-grad-${uid})`}
                      strokeWidth="6"
                      fill="none"
                      strokeLinecap="round"
                      filter={`url(#hero-streak-glow-soft-${uid})`}
                      opacity="0.55"
                    />
                    <path
                      className="core-2"
                      d={PATH_ALT}
                      stroke={efColor3}
                      strokeWidth="1.5"
                      fill="none"
                      strokeLinecap="round"
                      opacity="0.75"
                    />
                    {/* Cabeza de cometa luminosa viajando por la estela */}
                    <g className="comet">
                      <circle r="9" fill={efColor} opacity="0.6" filter={`url(#hero-streak-glow-${uid})`}>
                        <animateMotion dur="6s" repeatCount="indefinite" rotate="auto">
                          <mpath href={`#hero-streak-path-${uid}`} />
                        </animateMotion>
                      </circle>
                      <circle r="3" fill="#ffffff">
                        <animateMotion dur="6s" repeatCount="indefinite" rotate="auto">
                          <mpath href={`#hero-streak-path-${uid}`} />
                        </animateMotion>
                      </circle>
                    </g>
                  </StreakSvg>
                )}
                <MainImg
                  src={mainImg}
                  alt={typeof c.titulo === 'string' ? c.titulo : ''}
                  $maxH={c.altura_imagen || '450px'}
                  $fit={c.fit_imagen_principal || 'contain'}
                  $pos={c.posicion_imagen_principal || 'center'}
                  $radius={c.radio_imagen_principal}
                />
              </ImgCol>
            );
          })()}
          <ContentCol $width={contentWidth} $align={textAlign} $alignTablet={textAlignTablet} $alignMobile={textAlignMobile} $center={blockCentered} $padding={c.padding_contenido || '2rem'}>
            {c.texto_extra_arriba && extraTextNode}
            {(() => {
            const tituloEl = (
            <Title
              $color={c.color_titulo || '#fff'}
              $size={c.tamano_titulo || '52px'}
              $weight={c.peso_titulo || '700'}
              $font={c.fuente_titulo}
              $lineHeight={c.interlineado_titulo || '1.15'}
              $align={titleAlign}
              $alignTablet={titleAlignTablet}
              $alignMobile={titleAlignMobile}
              $transform={c.transform_titulo || 'none'}
              $glow={c.titulo_iluminacion === true}
              $glowColor={c.titulo_iluminacion_color || secondary}
              $glowAnim={c.titulo_iluminacion_animar !== false}
              $subrayado={subrayadoCss({ ...c, alineacion_titulo: titleAlign })}
              dangerouslySetInnerHTML={{ __html: tl(c.titulo, 'Bienvenido') }}
            />
            );
            // El titular puede llevar un icono a su izquierda (el portátil del
            // diseño). Sin icono_titulo se pinta el título solo, como siempre.
            const iconoUrl = c.icono_titulo
              ? (c.icono_titulo.startsWith('http') ? c.icono_titulo : `${BACKEND_BASE}/${c.icono_titulo.replace(/^\//, '')}`)
              : null;
            if (!iconoUrl) return tituloEl;
            return (
              <TituloFila
                $align={c.icono_titulo_alineacion || 'center'}
                $gap={c.icono_titulo_gap}
                $gapMovil={c.icono_titulo_gap_movil}
                $justify={titleAlign === 'center' ? 'center' : (titleAlign === 'right' ? 'flex-end' : 'flex-start')}
              >
                <TituloIcono
                  src={iconoUrl}
                  alt=""
                  $ancho={c.icono_titulo_ancho}
                  $anchoMovil={c.icono_titulo_ancho_movil}
                />
                {tituloEl}
              </TituloFila>
            );
            })()}
            <Subtitle
              $color={c.color_subtitulo || '#fff'}
              $size={c.tamano_subtitulo || '19px'}
              $weight={c.peso_subtitulo || '300'}
              $font={c.fuente_subtitulo}
              $lineHeight={c.interlineado_subtitulo || '1.6'}
              $align={textAlign}
              $alignTablet={textAlignTablet}
              $alignMobile={textAlignMobile}
              $mb={c.margen_subtitulo}
              dangerouslySetInnerHTML={{ __html: tl(c.subtitulo, '') }}
            />
            {!c.texto_extra_arriba && extraTextNode}
            {ocultarBotones && textoElegirIdioma && (() => {
              const conBorde = c.borde_elegir_idioma === true;
              const borderColor = c.borde_elegir_idioma_color || '#ffffff';
              const borderGrosor = c.borde_elegir_idioma_grosor || '2px';
              const borderEstilo = c.borde_elegir_idioma_estilo || 'solid';
              const borderAncho = c.borde_elegir_idioma_ancho || '100%';
              const borderPadding = c.borde_elegir_idioma_padding || '0.6rem';
              // Outer wrapper para alinear el "ancho" del borde (que puede ser < 100%)
              return (
                <div style={{
                  marginTop: '1.2rem',
                  display: 'flex',
                  justifyContent: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center',
                }}>
                  <div style={{
                    width: conBorde ? borderAncho : 'auto',
                    maxWidth: '100%',
                    paddingTop: conBorde ? borderPadding : 0,
                    borderTop: conBorde ? `${borderGrosor} ${borderEstilo} ${borderColor}` : 'none',
                    textAlign: textAlign,
                    color: c.color_texto_elegir_idioma || '#fff',
                    fontSize: c.tamano_texto_elegir_idioma || '0.95rem',
                    fontWeight: 600,
                    opacity: 0.9,
                    letterSpacing: '0.3px',
                  }}>
                    {textoElegirIdioma}
                  </div>
                </div>
              );
            })()}
            <BtnRow $justify={btnJustify} $justifyTablet={btnJustifyTablet} $justifyMobile={btnJustifyMobile} $mt={c.margen_botones} style={{ display: ocultarBotones ? 'none' : undefined }}>
              {mostrarBtnLogin && (
                <Btn
                  $bg={c.color_boton_login || secondary}
                  $color={c.color_texto_boton_login || '#fff'}
                  $padding={c.padding_boton_login || c.padding_botones}
                  $radius={c.radio_boton_login || c.radio_botones || '8px'}
                  $size={sizeLoginR.desktop || undefined}
                  $sizeTablet={sizeLoginR.tablet || undefined}
                  $sizeMobile={sizeLoginR.mobile || undefined}
                  $border={c.borde_boton_login || c.borde_botones}
                  $transform={c.transform_boton_login || 'none'}
                  onClick={() => handleAction(c.accion_boton_login || 'login')}
                >
                  {textoLogin}
                </Btn>
              )}
              {mostrarBtnRegistro && (
                <Btn
                  $bg={c.color_boton_registro || secondary}
                  $color={c.color_texto_boton_registro || '#fff'}
                  $padding={c.padding_boton_registro || c.padding_botones}
                  $radius={c.radio_boton_registro || c.radio_botones || '8px'}
                  $size={sizeRegistroR.desktop || undefined}
                  $sizeTablet={sizeRegistroR.tablet || undefined}
                  $sizeMobile={sizeRegistroR.mobile || undefined}
                  $border={c.borde_boton_registro || c.borde_botones}
                  $transform={c.transform_boton_registro || 'none'}
                  onClick={() => handleAction(c.accion_boton_registro || 'registro')}
                >
                  {textoRegistro}
                </Btn>
              )}
            </BtnRow>
            {/* Selector de idiomas debajo del contenido (configurable desde HeroForm).
                Los idiomas vienen de proyecto.config_json.idiomas_disponibles; el admin
                tilda en HeroForm cuáles mostrar (idiomas_seleccionados) y opcionalmente
                personaliza label/flag/color por code (idiomas_overrides). */}
            {(() => {
              if (!c.mostrar_idiomas) return null;
              const idiomasProyecto = (proyecto?.config_json?.idiomas_disponibles && proyecto.config_json.idiomas_disponibles.length)
                ? proyecto.config_json.idiomas_disponibles
                : (proyecto?.config_json?.idiomas_habilitados || []);
              const seleccionados = Array.isArray(c.idiomas_seleccionados) ? c.idiomas_seleccionados : idiomasProyecto;
              const codes = idiomasProyecto.filter(code => seleccionados.includes(code));
              if (codes.length === 0) return null;
              const overrides = c.idiomas_overrides || {};
              const estilo = c.estilo_idiomas || 'flags';
              // Color de texto: 'claro' (#fff) / 'oscuro' (#1f2937) / hex custom
              const textoIdiomas = c.color_texto_idiomas === 'oscuro'
                ? '#1f2937'
                : (c.color_texto_idiomas && c.color_texto_idiomas !== 'claro' ? c.color_texto_idiomas : '#fff');
              const conBorde = c.borde_banderas !== false; // default true
              const tamañoBandera = c.tamano_banderas || '64px';
              const colorBorde = c.color_borde_banderas || '#fff';
              const colorActivo = c.color_activo_idiomas || '#14b6cb';

              if (estilo === 'flags') {
                // Estilo "shield" — banderas redondeadas estilo escudo, label debajo
                return (
                  <IdiomasRow $justify={c.alineacion_idiomas || c.alineacion_texto || 'flex-start'} $mt={c.margen_idiomas} $gap="1.5rem">
                    {codes.map(code => {
                      const def = IDIOMA_DEFAULTS[code] || { label: code.toUpperCase(), flag_url: '', color: '#0f355b' };
                      const ov = overrides[code] || {};
                      const label = ov.label || def.label;
                      const sublabel = ov.sublabel || '';
                      const flagUrl = resolveUrl(ov.flag_url) || def.flag_url;
                      const activo = idioma === code;
                      return (
                        <IdiomaShield key={code} onClick={() => onSelectIdioma(code)} title={label}>
                          <IdiomaShieldImg
                            $flag={flagUrl}
                            $size={tamañoBandera}
                            $showBorder={conBorde}
                            $borderColor={colorBorde}
                            $active={activo}
                            $activeColor={colorActivo}
                          />
                          <IdiomaShieldLabel $active={activo} $color={textoIdiomas} $size={c.tamano_texto_idiomas || '0.85rem'}>
                            {label}
                            {sublabel && <small>{sublabel}</small>}
                          </IdiomaShieldLabel>
                        </IdiomaShield>
                      );
                    })}
                  </IdiomasRow>
                );
              }
              // Estilos "toggle" y "pills"
              const pillar = estilo === 'pills';
              return (
                <IdiomasRow $justify={c.alineacion_idiomas || c.alineacion_texto || 'flex-start'} $mt={c.margen_idiomas} $gap="0.6rem">
                  {codes.map(code => {
                    const def = IDIOMA_DEFAULTS[code] || { label: code.toUpperCase(), flag_url: '', color: '#0f355b' };
                    const ov = overrides[code] || {};
                    const label = ov.label || def.label;
                    const color = ov.color || def.color;
                    const activo = idioma === code;
                    return (
                      <IdiomaBtn
                        key={code}
                        $active={activo}
                        $color={color}
                        $textColor={textoIdiomas}
                        $pillar={pillar}
                        onClick={() => onSelectIdioma(code)}
                        title={label}
                      >
                        {label}
                      </IdiomaBtn>
                    );
                  })}
                </IdiomasRow>
              );
            })()}
          </ContentCol>
        </Inner>
      </Overlay>
    </Wrapper>
  );
};

export default HeroSection;
